/**
 * Hideable Admin Home card — headcount by platform role from org users.
 */
import { Link } from "react-router-dom";

import type { PeopleByRoleCounts } from "../peopleByRole";

import { Card, CardBody, CardHeader } from "@/components/ui/card/Card";
import { typography } from "@/design";
import { cn } from "@/lib/utils";

const ROWS: { key: keyof PeopleByRoleCounts; label: string }[] = [
  { key: "admin", label: "Admin" },
  { key: "member", label: "Member" },
  { key: "viewer", label: "Viewer" },
];

export type AdminHomePeopleByRoleProps = {
  counts: PeopleByRoleCounts | null;
  failed: boolean;
  /** True until the first read settles. A pending read renders nothing: it is
   * neither data nor "Unavailable", and the card appearing means data arrived. */
  loading?: boolean;
  /** The organization's real member count (not the size of the loaded page). */
  totalMembers?: number | null;
  /** How many members were actually loaded; fewer than the total = a partial page. */
  loadedMembers?: number | null;
};

export function AdminHomePeopleByRole({
  counts,
  failed,
  loading = false,
  totalMembers = null,
  loadedMembers = null,
}: AdminHomePeopleByRoleProps) {
  if (loading && !failed && counts === null) return null;
  const unavailable = failed || counts === null;
  // A page shorter than the real total cannot give the role split: counting
  // roles over it would be a silently capped sum.
  const partial =
    !failed &&
    typeof totalMembers === "number" &&
    typeof loadedMembers === "number" &&
    loadedMembers < totalMembers;

  return (
    <Card data-testid="admin-home-people-by-role">
      <CardHeader className="flex-row items-center justify-between space-y-0 border-b border-slate-100 p-5">
        <div className="space-y-1">
          <h2 className={typography.sectionTitle}>People by role</h2>
          <p className={typography.muted}>
            Organization membership by platform role.
          </p>
        </div>
        <Link
          to="/administration/people"
          className={cn(typography.label, "text-blue-700 hover:text-blue-800")}
        >
          Open People
        </Link>
      </CardHeader>
      <CardBody className="p-5">
        {typeof totalMembers === "number" && !failed ? (
          <p
            className={cn(typography.muted, partial ? undefined : "mb-3")}
            data-testid={
              partial ? "admin-home-people-partial" : "admin-home-people-total"
            }
          >
            {totalMembers} {totalMembers === 1 ? "member" : "members"}
            {partial
              ? ` · showing first ${loadedMembers} — role counts unavailable for the full roster.`
              : ""}
          </p>
        ) : null}
        {partial ? null : unavailable ? (
          <p
            className={typography.muted}
            data-testid="admin-home-people-unavailable"
          >
            Unavailable
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {ROWS.map(({ key, label }) => (
              <li
                key={key}
                className="rounded-lg border border-slate-200 px-4 py-3"
                data-testid={`admin-home-people-role-${key}`}
              >
                <p className={typography.muted}>{label}</p>
                <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
                  {counts[key]}
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
